import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export enum FilterGroupType {
  Multiple = 'multiple',
  Single = 'single',
}

export interface ServerFilter {
  name: string;
  value: string;
  group: string;
  isActive: boolean;
}

export interface ServerFilterGroup {
  name: string;
  value: string;
  type: FilterGroupType;
  filters: ServerFilter[];
}

export interface RawServerFilterConfig {
  filterGroups: RawServerFilterGroup[];
  selectedServerFilters?: { [key: string]: string[] };
}

export interface RawServerFilterGroup {
  name: string;
  type: FilterGroupType;
  filters: { [key: string]: string };
}

@Injectable({ providedIn: 'root' })
export class PredefinedFiltersService {
  public hasFilters = false;
  private queryParamFilters: string[] = [];
  private filterGroups: ServerFilterGroup[] = [];
  private filtersObservable = new BehaviorSubject(this.filterGroups);

  public constructor(private translate: TranslateService, private activeRoute: ActivatedRoute) {
    this.activeRoute.queryParamMap.subscribe({
      next: (params) => {
        this.setQueryParamFilters(params.get('predefinedFilters')?.split(',') ?? []);
      },
    });
  }

  public setupNewFilters(rawFilterConfig: RawServerFilterConfig): void {
    const filterGroups: ServerFilterGroup[] = this.getFilterGroupsFromRawConfig(rawFilterConfig);

    if (this.isNewFilterSet(filterGroups)) {
      this.hasFilters = true;
      this.filterGroups = filterGroups;
      this.filtersObservable.next(this.filterGroups);
    }
  }

  private getFilterGroupsFromRawConfig(rawFilterConfig: RawServerFilterConfig): ServerFilterGroup[] {
    const filterGroups: ServerFilterGroup[] = [];

    for (const [groupKey, groupData] of Object.entries(rawFilterConfig.filterGroups)) {
      const filters: ServerFilter[] = [];

      for (const [filterValue, filterLabel] of Object.entries(groupData.filters)) {
        const isPreEnabled = this.isFilterPreEnabled(filterValue, rawFilterConfig.selectedServerFilters);

        filters.push({
          name: this.translate.instant('FILTERS')[filterLabel] ?? filterLabel,
          value: filterValue,
          group: this.translate.instant('FILTERS')[groupData.name] ?? groupData.name,
          isActive: isPreEnabled,
        });
      }

      filterGroups.push({
        name: this.translate.instant('FILTERS')[groupData.name] ?? groupData.name,
        value: groupKey,
        type: groupData.type ?? FilterGroupType.Multiple,
        filters: filters,
      });
    }

    return filterGroups;
  }

  private isNewFilterSet(filterGroups: ServerFilterGroup[]): boolean {
    const existingFilters = this.filterGroups.map((filterGroup: ServerFilterGroup) => {
      return filterGroup.filters.map((filter: ServerFilter) => {
        delete filter.isActive;
        return filter;
      });
    });

    const newFilters = filterGroups.map((filterGroup: ServerFilterGroup) => {
      return filterGroup.filters.map((filter: ServerFilter) => {
        delete filter.isActive;
        return filter;
      });
    });

    return JSON.stringify(existingFilters) !== JSON.stringify(newFilters);
  }

  public resetState(): void {
    this.hasFilters = false;
    this.filterGroups = [];
  }

  public isFilterPreEnabled(
    filter: string,
    filterPreferences: RawServerFilterConfig['selectedServerFilters']
  ): boolean {
    let isEnabled = false;
    const queryParams = this.activeRoute.snapshot.queryParamMap.get('predefinedFilters')?.split(',') ?? [];
    const preferredFilters = filterPreferences ? [].concat.apply([], Object.values(filterPreferences)) : [];

    if (queryParams.includes(filter)) {
      isEnabled = true;
    }

    if (preferredFilters.includes(filter)) {
      isEnabled = true;
    }

    return isEnabled;
  }

  public disableFilters(filters: string | string[]): void {
    filters = Array.isArray(filters) ? filters : [filters];
    const allFilters = this.getFiltersFromGroups(this.filterGroups);
    const targetFilters = allFilters.filter((filterObject: ServerFilter) => filters.includes(filterObject.value));

    targetFilters.forEach((filter) => (filter.isActive = false));

    this.filtersObservable.next(this.filterGroups);
  }

  public enableFilters(filters: string | string[]): void {
    filters = Array.isArray(filters) ? filters : [filters];
    const allFilters = this.getFiltersFromGroups(this.filterGroups);
    const targetFilters = allFilters.filter((filterObject: ServerFilter) => filters.includes(filterObject.value));

    targetFilters.forEach((filter) => (filter.isActive = true));

    this.filtersObservable.next(this.filterGroups);
  }

  public getAllFilters(): Observable<ServerFilter[]> {
    return this.filtersObservable.pipe(
      map((filterGroups: ServerFilterGroup[]) => {
        return this.getFiltersFromGroups(filterGroups);
      })
    );
  }

  public getActiveFilters(): Observable<ServerFilter[]> {
    return this.getAllFilters().pipe(
      map((filters: ServerFilter[]) => {
        return filters.filter((filter: ServerFilter) => filter.isActive);
      })
    );
  }

  public getGroupedFilters(): Observable<ServerFilterGroup[]> {
    return this.filtersObservable;
  }

  public getGroupedActiveFilterValues(): Observable<{ [key: string]: string[] }> {
    return this.getGroupedFilters().pipe(
      map((filterGroups: ServerFilterGroup[]) => {
        const groups = {};

        filterGroups.forEach((filterGroup) => {
          const activeFilters = filterGroup.filters.filter((filter) => filter.isActive);
          groups[filterGroup.value] = activeFilters.map((filter) => filter.value);
        });

        return groups;
      })
    );
  }

  private getFiltersFromGroups(filterGroups: ServerFilterGroup[]): ServerFilter[] {
    return [].concat.apply(
      [],
      filterGroups.map((filterGroup: ServerFilterGroup) => filterGroup.filters)
    );
  }

  private setQueryParamFilters(filters: string[]): void {
    const filtersToRemove = this.queryParamFilters.filter((filter) => !filters.includes(filter));

    this.disableFilters(filtersToRemove);
    this.enableFilters(filters);
    this.queryParamFilters = filters;
  }
}
